import React from 'react';

import { Avatar, Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';

import useImageUrlGenerator from '~/hooks/useImageUrlGenerator';

interface Props {
  id: string | null;
  title?: string;
  imageId?: string;
  imageUrl?: string;
  isLoading: boolean;
  isOnline?: boolean;
}

export default function TagAssociationOfferCell({ id, title, imageId, imageUrl, isLoading, isOnline }: Props) {
  const thumbnailUrl = useImageUrlGenerator(imageId, { width: 80, height: 80 });
  const statusLabel = isOnline === undefined ? 'Status unavailable' : isOnline ? 'Online' : 'Offline';
  const statusColor = isLoading || isOnline === undefined ? 'text.disabled' : isOnline ? 'success.light' : 'error.main';

  if (!id) {
    return <Typography color="text.secondary">—</Typography>;
  }

  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
      <Avatar
        alt={title || 'Offer image'}
        src={thumbnailUrl || imageUrl}
        variant="rounded"
        slotProps={{ img: { loading: 'lazy' } }}
      />
      <Stack direction="row" spacing={1} sx={{ minWidth: 0 }}>
        <Tooltip title={isLoading ? 'Loading status' : statusLabel}>
          <Box
            component="span"
            role="img"
            tabIndex={0}
            aria-label={isLoading ? 'Loading status' : statusLabel}
            sx={{ width: 4, flexShrink: 0, borderRadius: 1, bgcolor: statusColor, my: 0.25 }}
          />
        </Tooltip>
        <Stack sx={{ minWidth: 0 }}>
          {isLoading && !title && <Skeleton width={160} />}
          {(!isLoading || !!title) && (
            <Typography variant="body2" fontWeight="bold" noWrap title={title}>
              {title || 'Offer unavailable'}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" noWrap title={id}>
            {id}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}
